import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../sections/Footer";
import { projects } from "../data/portfolio";

/**
 * Shared case study template (v3) — left-rail TOC + pillar cards + marker highlights.
 * Data-driven from portfolio.js. Used by UniAcco PDP, Donna, BodySignal.
 */

const INK = "#0E1A2B";
const MAGENTA = "#E5326A";
const YELLOW_SOFT = "#FFF7C4";
const MAGENTA_SOFT = "#FDECF2";
const WHITE = "#FFFFFF";
const TINT = "#F7F7FA";
const MUTED = "#6E7583";
const LINE = "#E4E6EC";

const bodyFont = { fontFamily: "'Inter', system-ui, sans-serif" };
const displayFont = {
  fontFamily: "'Manrope', 'Inter', sans-serif",
  fontWeight: 800,
  letterSpacing: "-0.03em",
  lineHeight: 1.15,
};
const monoFont = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

/* ---------- Blocks ---------- */

const Marker = ({ children, tone = "magenta" }) => {
  const c = tone === "yellow" ? { bg: YELLOW_SOFT, ink: INK } : { bg: MAGENTA_SOFT, ink: MAGENTA };
  return (
    <span
      className="rounded-[3px] px-1.5"
      style={{
        background: c.bg,
        color: c.ink,
        fontWeight: 600,
        boxDecorationBreak: "clone",
        WebkitBoxDecorationBreak: "clone",
        paddingTop: "0.02em",
        paddingBottom: "0.02em",
      }}
    >
      {children}
    </span>
  );
};

const Eyebrow = ({ children }) => (
  <div className="text-[11px] tracking-[0.22em] uppercase mb-6 font-medium" style={{ color: MUTED, ...bodyFont }}>
    {children}
  </div>
);

const PillarCard = ({ n, tag, title, body }) => (
  <div className="rounded-2xl p-6 border h-full flex flex-col" style={{ background: WHITE, borderColor: LINE }}>
    <div className="text-[11px] tracking-[0.22em] uppercase mb-6" style={{ color: MUTED, ...bodyFont, fontWeight: 500 }}>
      {n} / {tag}
    </div>
    <div className="mb-3" style={{ ...displayFont, fontSize: 20, color: INK, lineHeight: 1.2 }}>{title}</div>
    <p className="text-sm" style={{ color: MUTED, ...bodyFont, lineHeight: 1.55 }}>{body}</p>
  </div>
);

const Blockquote = ({ children }) => (
  <blockquote
    className="my-10 border-l-2 pl-6 py-2 text-lg md:text-xl leading-[1.55]"
    style={{ borderColor: INK, color: INK, ...bodyFont, fontWeight: 600 }}
  >
    “{children}”
  </blockquote>
);

const MetaRow = ({ role, timeframe, team, platform }) => (
  <div className="my-10 border-y" style={{ borderColor: LINE }}>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6">
      {[
        { k: "Role", v: role },
        { k: "Timeframe", v: timeframe },
        { k: "Team", v: team },
        { k: "Platform", v: platform },
      ].map((m) => (
        <div key={m.k}>
          <div className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: MUTED, ...bodyFont, fontWeight: 500 }}>{m.k}</div>
          <div className="text-sm" style={{ color: INK, ...bodyFont }}>{m.v}</div>
        </div>
      ))}
    </div>
  </div>
);

const StatTile = ({ value, label }) => (
  <div className="p-5 rounded-2xl border" style={{ background: WHITE, borderColor: LINE }}>
    <div style={{ ...monoFont, fontWeight: 500, fontSize: 28, lineHeight: 1, color: MAGENTA }}>{value}</div>
    <div className="mt-2 text-xs" style={{ color: MUTED, ...bodyFont, fontWeight: 500 }}>{label}</div>
  </div>
);

const ProductFrame = ({ src, alt, caption }) => (
  <figure className="my-8 md:my-10">
    <div className="rounded-2xl overflow-hidden border" style={{ borderColor: LINE, background: TINT }}>
      <img src={src} alt={alt} className="block w-full h-auto" loading="lazy" />
    </div>
    {caption && <figcaption className="mt-3 text-xs md:text-sm" style={{ color: MUTED, ...bodyFont }}>{caption}</figcaption>}
  </figure>
);

const YouCursor = () => (
  <div aria-hidden className="fixed left-8 bottom-14 z-40 items-center gap-2 pointer-events-none hidden md:flex">
    <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
      <path d="M2 2L18 12L10 14L6 22L2 2Z" fill={INK} />
    </svg>
    <span className="rounded-full px-3 py-1 text-xs font-medium shadow-lg" style={{ background: INK, color: WHITE, ...bodyFont }}>You</span>
  </div>
);

const LeftRail = ({ chapters, active, onJump }) => (
  <aside className="hidden lg:block sticky top-32 self-start" aria-label="Case study chapters" data-testid="case-toc">
    <button
      onClick={() => (window.history.length > 1 ? window.history.back() : (window.location.href = "/#work"))}
      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium mb-10"
      style={{ background: WHITE, color: INK, border: `1px solid ${LINE}`, ...bodyFont }}
      data-testid="case-back"
    >
      <ArrowLeft size={14} /> Back
    </button>
    <nav className="flex flex-col gap-1">
      {chapters.map((c) => {
        const isActive = c.id === active;
        return (
          <button
            key={c.id}
            onClick={() => onJump(c.id)}
            className="grid grid-cols-[36px_1fr] items-center gap-2 rounded-lg px-3 py-2 text-left transition"
            style={{
              background: isActive ? MAGENTA_SOFT : "transparent",
              color: isActive ? INK : MUTED,
              ...bodyFont,
              fontWeight: isActive ? 600 : 500,
            }}
            data-testid={`case-toc-${c.n}`}
          >
            <span style={{ ...monoFont, fontSize: 12, color: isActive ? MAGENTA : MUTED }}>{c.n}</span>
            <span className="text-sm">{c.label}</span>
          </button>
        );
      })}
    </nav>
  </aside>
);

const S = ({ id, children }) => (
  <section id={id} data-testid={id} className="scroll-mt-32 py-14 md:py-16 border-t" style={{ borderColor: LINE }}>
    {children}
  </section>
);

/* ---------- Root ---------- */

export const CaseStudy = () => {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);
  const [active, setActive] = useState("ch-01");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  const chapters = React.useMemo(() => {
    const s = project?.study;
    if (!s) return [];
    const arr = [
      { id: "ch-01", label: "Business Context" },
      { id: "ch-02", label: "The Challenge" },
      { id: "ch-03", label: "My Approach" },
      { id: "ch-04", label: "Key Decisions" },
    ];
    if (s.howIWorked?.length) arr.push({ id: "ch-05", label: "Team Alignment" });
    arr.push({ id: "ch-06", label: "Results" });
    arr.push({ id: "ch-07", label: "Learnings" });
    arr.push({ id: "ch-08", label: "Next Step" });
    // Re-index sequentially so numbers stay 01, 02, 03… even if a chapter is skipped
    return arr.map((c, i) => ({ ...c, n: String(i + 1).padStart(2, "0") }));
  }, [project]);

  useEffect(() => {
    if (!chapters.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    chapters.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [chapters]);

  if (!project || !project.study) return <Navigate to="/" replace />;
  const study = project.study;

  const idx = projects.findIndex((p) => p.id === id);
  const next = projects[(idx + 1) % projects.length];

  const jumpTo = (t) => {
    const el = document.getElementById(t);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const problemFlat = study.problem || [];

  return (
    <div style={{ background: WHITE, minHeight: "100vh", color: INK, ...bodyFont }} data-testid={`case-study-${project.id}`}>
      <Navigation />
      <YouCursor />
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 pt-28">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-3">
            <LeftRail chapters={chapters} active={active} onJump={jumpTo} />
          </div>
          <main className="col-span-12 lg:col-span-9 max-w-3xl">
            {/* Ch01 Business Context */}
            <S id="ch-01">
              <Eyebrow>{project.category}</Eyebrow>
              <h1 style={{ ...displayFont, fontSize: "clamp(2.2rem, 4.5vw, 4rem)", color: INK, lineHeight: 1.15 }} data-testid="case-title">
                {project.title}
              </h1>
              <p className="mt-6 text-base md:text-lg" style={{ color: INK, ...bodyFont, lineHeight: 1.65 }}>{project.summary}</p>
              <MetaRow role={project.role} timeframe={study.timeframe} team={study.team} platform={study.platform} />
              {study.impactTop?.length ? (
                <div className={`grid grid-cols-2 ${study.impactTop.length > 3 ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}>
                  {study.impactTop.map((m, i) => <StatTile key={i} value={m.value} label={m.label} />)}
                </div>
              ) : null}
              {project.image && <ProductFrame src={project.image} alt={project.title} />}
            </S>

            {/* Ch02 The Challenge */}
            <S id="ch-02">
              <Eyebrow>The Challenge</Eyebrow>
              <h2 style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.2vw, 3.2rem)", color: INK, lineHeight: 1.35 }}>
                What was actually <Marker tone="magenta">broken.</Marker>
              </h2>
              <ul className="mt-8 space-y-4">
                {problemFlat.slice(0, 6).map((p, i) => (
                  <li key={i} className="flex gap-3 text-base" style={{ color: INK, ...bodyFont, lineHeight: 1.6 }}>
                    <span style={{ ...monoFont, color: MAGENTA, fontSize: 12, paddingTop: 4 }}>{String(i + 1).padStart(2, "0")}</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </S>

            {/* Ch03 My Approach */}
            {study.approach?.length ? (
              <S id="ch-03">
                <Eyebrow>My Approach</Eyebrow>
                <h2 style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.2vw, 3.2rem)", color: INK, lineHeight: 1.35 }}>
                  How I got it <Marker tone="magenta">unstuck.</Marker>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
                  {study.approach.slice(0, 6).map((a, i) => (
                    <PillarCard key={i} n={String(i + 1).padStart(2, "0")} tag="Principle" title={a.title} body={a.body} />
                  ))}
                </div>
              </S>
            ) : null}

            {/* Ch04 Key Decisions */}
            {study.decisions?.length ? (
              <S id="ch-04">
                <Eyebrow>Key Decisions</Eyebrow>
                <h2 style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.2vw, 3.2rem)", color: INK, lineHeight: 1.35 }}>
                  The moves that <Marker tone="magenta">did the work.</Marker>
                </h2>
                <div className="grid grid-cols-1 gap-6 mt-10">
                  {study.decisions.map((d) => (
                    <div key={d.id} className="rounded-2xl p-6 md:p-8 border" style={{ background: WHITE, borderColor: LINE }}>
                      <div className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: MAGENTA, ...monoFont, fontWeight: 500 }}>{d.id}</div>
                      <div className="mb-3" style={{ ...displayFont, fontSize: 20, color: INK, lineHeight: 1.2 }}>{d.title}</div>
                      <p className="text-sm md:text-base mb-3 max-w-xl" style={{ color: INK, ...bodyFont, lineHeight: 1.55 }}>{d.after}</p>
                      {d.why && <p className="text-xs md:text-sm mb-4 max-w-xl" style={{ color: MUTED, ...bodyFont, lineHeight: 1.5 }}><strong>Why:</strong> {d.why}</p>}
                      {d.beforeImage && d.afterImage ? (
                        <div className="grid grid-cols-2 gap-4 mt-2">
                          <figure>
                            <div className="rounded-xl overflow-hidden border" style={{ borderColor: LINE }}>
                              <img src={d.beforeImage} alt={`${d.title} — before`} className="block w-full h-auto" loading="lazy" />
                            </div>
                            <figcaption className="mt-1.5 text-[10px] tracking-[0.18em] uppercase" style={{ color: MUTED, ...bodyFont }}>Before</figcaption>
                          </figure>
                          <figure>
                            <div className="rounded-xl overflow-hidden border" style={{ borderColor: LINE }}>
                              <img src={d.afterImage} alt={`${d.title} — after`} className="block w-full h-auto" loading="lazy" />
                            </div>
                            <figcaption className="mt-1.5 text-[10px] tracking-[0.18em] uppercase" style={{ color: MUTED, ...bodyFont }}>After</figcaption>
                          </figure>
                        </div>
                      ) : d.builtImage ? (
                        <div className="rounded-xl overflow-hidden border mt-2" style={{ borderColor: LINE }}>
                          <img src={d.builtImage} alt={d.title} className="block w-full h-auto" loading="lazy" />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </S>
            ) : null}

            {/* Ch05 Team Alignment */}
            {study.howIWorked?.length ? (
              <S id="ch-05">
                <Eyebrow>Team Alignment</Eyebrow>
                <h2 style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.2vw, 3.2rem)", color: INK, lineHeight: 1.35 }}>
                  How I <Marker tone="magenta">moved the room.</Marker>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  {study.howIWorked.map((c, i) => (
                    <div key={i} className="rounded-2xl p-6 border" style={{ background: TINT, borderColor: LINE }}>
                      <div className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: MAGENTA, ...monoFont, fontWeight: 500 }}>C—{String(i + 1).padStart(2, "0")}</div>
                      <div className="mb-2" style={{ ...displayFont, fontSize: 18, color: INK, lineHeight: 1.2 }}>{c.title}</div>
                      <p className="text-sm" style={{ color: MUTED, ...bodyFont, lineHeight: 1.55 }}>{c.body}</p>
                    </div>
                  ))}
                </div>
              </S>
            ) : null}

            {/* Ch06 Results */}
            <S id="ch-06">
              <Eyebrow>Results</Eyebrow>
              <div className="rounded-2xl p-8 md:p-12 border" style={{ background: TINT, borderColor: LINE }}>
                {study.impactTop?.length ? (
                  <div className="mb-6" style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.6vw, 3.6rem)", color: INK, lineHeight: 1.35 }}>
                    <span style={{ color: MAGENTA }}>{study.impactTop.slice(0, 3).map((m) => m.value).join(" · ")}</span>: {project.title}.
                  </div>
                ) : null}
                {study.impactProvenance && (
                  <p className="max-w-2xl text-base md:text-lg" style={{ color: INK, ...bodyFont, lineHeight: 1.6 }}>{study.impactProvenance}</p>
                )}
              </div>
            </S>

            {/* Ch07 Learnings */}
            {(study.reflection || study.learnings?.length) ? (
              <S id="ch-07">
                <Eyebrow>Learnings</Eyebrow>
                {study.reflection && <Blockquote>{study.reflection}</Blockquote>}
                {study.learnings?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    {study.learnings.map((l, i) => (
                      <div key={i} className="rounded-2xl p-6 border" style={{ background: WHITE, borderColor: LINE }}>
                        <p className="text-sm" style={{ color: INK, ...bodyFont, lineHeight: 1.55 }}>{l}</p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </S>
            ) : null}

            {/* Ch08 Next Step */}
            <S id="ch-08">
              <Eyebrow>Next Step</Eyebrow>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                <div>
                  <div className="text-sm mb-2" style={{ color: MUTED, ...bodyFont }}>Continue reading</div>
                  <div style={{ ...displayFont, fontSize: "clamp(1.8rem, 3.6vw, 3.6rem)", color: INK, lineHeight: 1.15 }}>{next.title}</div>
                  <div className="mt-2 text-xs tracking-[0.22em] uppercase" style={{ color: MUTED, ...bodyFont }}>{next.category}</div>
                </div>
                <Link
                  to={`/work/${next.id}`}
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
                  style={{ background: INK, color: WHITE, ...bodyFont }}
                  data-testid="case-next-link"
                >
                  Open case study <ArrowUpRight size={14} />
                </Link>
              </div>
            </S>
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CaseStudy;
